import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import {
  collection, getDocs, addDoc, updateDoc, deleteDoc,
  doc, Timestamp, query, orderBy
} from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';
import { useConfig } from '../contexts/ConfigContext';
import LoadingAnimation from './LoadingAnimation';

const PhotoCard = ({ photo, index, total, onMove, onDelete, onSaveCaption }) => {
  const [imgLoaded, setImgLoaded] = useState(false);
  const [editingCaption, setEditingCaption] = useState(false);
  const [captionDraft, setCaptionDraft] = useState(photo.caption || '');

  const handleSave = () => {
    onSaveCaption(photo.id, captionDraft);
    setEditingCaption(false);
  };

  return (
    <div style={{ border: '1px solid #ddd', overflow: 'hidden' }}>
      <div style={{ position: 'relative', width: '100%', height: '140px', background: '#f0f0f0' }}>
        {!imgLoaded && (
          <div style={{
            position: 'absolute', inset: 0, display: 'flex',
            alignItems: 'center', justifyContent: 'center'
          }}>
            <div className="stt-spinner" />
          </div>
        )}
        <img
          src={photo.url}
          alt={photo.caption || ''}
          onLoad={() => setImgLoaded(true)}
          style={{
            width: '100%', height: '140px', objectFit: 'cover', display: 'block',
            opacity: imgLoaded ? 1 : 0, transition: 'opacity 0.2s'
          }}
        />
      </div>
      <div style={{ padding: '8px' }}>
        {editingCaption ? (
          <div style={{ marginBottom: '8px' }}>
            <input
              type="text"
              className="form-control form-control-sm"
              value={captionDraft}
              onChange={e => setCaptionDraft(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') setEditingCaption(false); }}
              placeholder="Add a caption…"
              autoFocus
            />
            <div style={{ display: 'flex', gap: '4px', marginTop: '4px' }}>
              <button className="btn btn-sm btn-outline-secondary" onClick={handleSave}>Save</button>
              <button className="btn btn-sm btn-outline-secondary" onClick={() => setEditingCaption(false)}>Cancel</button>
            </div>
          </div>
        ) : (
          <p
            style={{ fontSize: '0.78rem', color: photo.caption ? '#555' : '#aaa', margin: '0 0 8px', cursor: 'pointer' }}
            onClick={() => setEditingCaption(true)}
          >
            {photo.caption || 'Add caption…'}
          </p>
        )}
        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
          <button className="btn btn-sm btn-outline-secondary" onClick={() => onMove(index, -1)} disabled={index === 0}>↑</button>
          <button className="btn btn-sm btn-outline-secondary" onClick={() => onMove(index, 1)} disabled={index === total - 1}>↓</button>
          <button className="btn btn-sm btn-outline-danger" onClick={() => onDelete(photo.id)}>Remove</button>
        </div>
      </div>
    </div>
  );
};

const AdminGallery = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const config = useConfig();

  const [loading, setLoading] = useState(true);
  const [photos, setPhotos] = useState([]);
  const [caption, setCaption] = useState('');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        navigate('/login', { state: { from: location.pathname } });
      } else if (config.configLoaded && config.adminEmails.length > 0 && !config.adminEmails.includes(user.email)) {
        navigate('/');
      } else if (config.configLoaded) {
        fetchPhotos().then(() => setLoading(false));
      }
    });
    return () => unsubscribe();
  }, [config.configLoaded, config.adminEmails]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchPhotos = async () => {
    const snap = await getDocs(query(collection(db, 'gallery'), orderBy('order', 'asc')));
    setPhotos(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  };

  const handleUpload = async () => {
    if (!file) { setSaveMsg('Please select an image.'); return; }
    setSaving(true);
    setUploading(true);
    setSaveMsg('');

    try {
      const storageRef = ref(storage, `gallery/${Date.now()}_${file.name}`);
      const task = uploadBytesResumable(storageRef, file);

      await new Promise((resolve, reject) => {
        task.on('state_changed',
          snap => setUploadProgress(Math.round(snap.bytesTransferred / snap.totalBytes * 100)),
          reject,
          resolve
        );
      });

      const url = await getDownloadURL(task.snapshot.ref);
      const maxOrder = photos.reduce((max, p) => Math.max(max, p.order || 0), 0);

      await addDoc(collection(db, 'gallery'), {
        url,
        caption: caption.trim(),
        order: maxOrder + 1,
        created_at: Timestamp.fromDate(new Date())
      });

      setCaption('');
      setFile(null);
      setUploadProgress(0);
      setSaveMsg('Uploaded!');
      await fetchPhotos();
    } catch (err) {
      console.error(err);
      setSaveMsg('Upload failed. Please try again.');
    } finally {
      setSaving(false);
      setUploading(false);
    }
  };

  const handleSaveCaption = async (id, caption) => {
    await updateDoc(doc(db, 'gallery', id), { caption: caption.trim() });
    await fetchPhotos();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this photo from the gallery?')) return;
    await deleteDoc(doc(db, 'gallery', id));
    await fetchPhotos();
  };

  const movePhoto = async (index, direction) => {
    const swapIndex = index + direction;
    if (swapIndex < 0 || swapIndex >= photos.length) return;
    const a = photos[index];
    const b = photos[swapIndex];
    await Promise.all([
      updateDoc(doc(db, 'gallery', a.id), { order: b.order ?? swapIndex }),
      updateDoc(doc(db, 'gallery', b.id), { order: a.order ?? index })
    ]);
    await fetchPhotos();
  };

  if (loading) return <div style={{ minHeight: '100vh' }}><LoadingAnimation /></div>;

  return (
    <div className="stt-admin-page">
      <a href="/admin" className="stt-admin-back">← Back to Admin</a>
      <h1 className="stt-admin-title">Photo Gallery</h1>
      <hr />

      {/* Photo grid */}
      {photos.length > 0 && (
        <div className="stt-admin-card">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '12px' }}>
            {photos.map((photo, i) => (
              <PhotoCard
                key={photo.id}
                photo={photo}
                index={i}
                total={photos.length}
                onMove={movePhoto}
                onDelete={handleDelete}
                onSaveCaption={handleSaveCaption}
              />
            ))}
          </div>
        </div>
      )}

      {/* Upload form */}
      <div className="stt-admin-card">
        <h2 style={{ fontFamily: 'var(--font-header)', fontSize: '1.1rem', marginBottom: '16px', color: 'var(--navy)' }}>
          {photos.length === 0 ? 'Upload First Photo' : 'Upload New Photo'}
        </h2>

        <div className="mb-3">
          <label className="stt-form-label">Image *</label>
          <input
            type="file"
            accept="image/*"
            className="form-control"
            onChange={e => setFile(e.target.files[0])}
            disabled={uploading}
          />
          {uploading && <p className="stt-upload-progress">Uploading… {uploadProgress}%</p>}
        </div>

        <div className="mb-3">
          <label className="stt-form-label">Caption</label>
          <p className="stt-form-hint">Optional</p>
          <input type="text" className="form-control" value={caption} onChange={e => setCaption(e.target.value)} />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button className="btn stt-btn-primary" onClick={handleUpload} disabled={saving}>
            {saving ? 'Uploading...' : 'Upload Photo'}
          </button>
          {saveMsg && (
            <span className={`stt-save-message ${saveMsg === 'Uploaded!' ? 'success' : 'error'}`}>
              {saveMsg}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminGallery;
